// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import {
  RawMaterialStatus,
  StorageHistoryStatus,
  StorageReceiveMethod,
} from './Enums';
import {
  EmissionWasteUnitKind,
  DashboardsPoolResourcesExportJobStatus,
  ImportStatus,
  ItemSize,
  OverallRank,
  PrintLabel,
  ScaleType,
  BookingQRType,
  CollectDeliveryStatus,
} from 'util/Enums';
import { OperatorType } from './Enums';
import { WasteRegistrationOfflineRequest } from 'types/offline';
import { ICCardItem } from 'components/organisms/AdminTenantManagement';
import { GetListParams } from 'types/api';
import { SelectOption } from 'components/molecules/SelectField';

export type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

type TraceRecycle = {
  id: string;
  weight: string;
  description: string;
  datetime: string;
  operatorName: string;
  plasticType: string;
  materialType: string;
  grade: string;
  siteName: string;
};
type TraceCompression = {
  id: string;
  weight: string;
  description: string;
  deliveredDatetime: string;
  deliveryToOperatorName: string;
  bookingDatetime: string;
  deliveryFromOperatorName: string;
  grade: string;
  deliveryFromSiteName: string;
};

type TraceEmission = {
  id: string;
  description: string;
  weight: string;
  deliveredDatetime: string;
  deliveryToOperatorName: string;
  collectedDatetime: string;
  collectedAreaName: string;
  deliveryToSiteName: string;
};

export type TraceData = {
  stage: OperatorType;
  stageIndex: number;
  data: TraceRecycle[] | TraceCompression[] | TraceEmission[];
};

export type wasteUnit = {
  id: string;
  type: string;
  unit: string;
  quantity: string;
  convertKg: string;
  price: string;
  inputUnit: string;
};

export type UncollectEmission = {
  id: string;
  operatorId: string;
  siteId: string;
  areaId: string;
  description: string;
  type: string;
  weight: string;
  datetime: string;
  wasteUnitId?: number;
  wasteUnitQuantity?: string;
  nextStage: boolean;
  wasteUnit?: WasteUnit;
  collectionOperatorName?: string;
  emissionStatus: string;
  // defined when it is fetched as substitute regist
  siteName?: string;
  tenant?:
    | (TenantBase & {
        tenantNumber: string;
        industry: string;
        isDashboardEnabled: boolean;
        contactName: string;
        contactEmail: string;
        icCards?: ICCardItem[] | null;
        barcode?: string | null;
      })
    | null;
  temporaryStorage?: { id: number | null; name: string; color?: string } | null;
  storageUpdatedAt?: string;
};

export type UnCollectEmissionObj = {
  page: number;
  total: number;
  data: UncollectEmission[];
};

export enum EmissionStatus {
  UnRequest = 'UnRequest',
  Requested = 'Requested',
  Close = 'Closed',
}
export type Emission = {
  datetime: string;
  operatorName: string;
  userId: string;
  userName: string;
  description: string;
  weight: string;
  emissionStatus: EmissionStatus;
};
export type EmissionRequest = {
  datetime: string;
  userId: string;
  userName: string;
};
export type EmissionCollect = {
  datetime: string;
  userId: string;
  userName: string;
  operatorName: string;
  ediNumber: string;
};
export type EmissionData = {
  id: string;
  emission: Emission;
  request?: EmissionRequest;
  collect?: EmissionCollect;
};

export type Site = {
  id: string;
  name: string;
  childSites: CommonSite[];
};

export type CommonSite = {
  id: string;
  name: string;
};

export type CollectHistory = {
  id: number;
  receivedDatetime: string;
  deliveredDatetime: string | undefined; // undefined if it has not been delivered
  areaName: string;
  userId: string;
  userName: string;
  description: string;
  weight: number;
  ediNumber: string;
  ediStatus: EdiStatus;
  receivedEdiStatus: EdiStatus;
  wasteUnitName?: string;
  deliveryStatus?: CollectDeliveryStatus;
  isModifiable: boolean;
};

export type ByTenant = Record<
  string,
  {
    name: string;
    weight: string;
    count: number;
    processingObjectIds: string[];
  }
>;

export type CollectByTenantMap = Record<string, ByTenant>;

export type CollectHistoryListResponse = {
  page: number;
  total: number;
  deliveries: CollectHistory[];
};

export type CompressionReceiveData = {
  id: string;
  receivedDatetime: string;
  userId: string;
  userName: string;
  deliveryFromAreaName: string;
  deliveryFromSiteName: string;
  description: string;
  wasteUnitName?: string;
  weight: string;
  targetType: string;
  targetIds: EdiIdMapper[];
  receptionMethod: string;
};

export type EdiIdMapper = {
  id: string;
  ediNumber?: string;
  ediStatus?: EdiStatus;
};

export enum EdiStatus {
  None = 'None',
  Success = 'Success',
  Failed = 'Failed',
  Processing = 'Processing',
  TransportDone = 'TransportDone',
  ProcessDone = 'ProcessDone',
  TransportError = 'TransportError',
  ProcessError = 'ProcessError',
  FinalDisposalDone = 'FinalDisposalDone',
}

export type CompressionCompleteData = {
  id: string;
  requestedDatetime: string;
  userId: string;
  userName: string;
  deliveryToOperatorName: string;
  description: string;
  wasteUnitName?: string;
  grade: string;
  weight: string;
  targetType: string;
  targetIds: string[];
  byCompression: Record<
    string,
    {
      primaryProcessingId: string;
      grade: string;
      weight: string;
      registeredDatetime: string;
      totalInputWeight: string;
    }
  >;
};

export type Compression = {
  compressionId: string;
  datetime: string;
  userId: string;
  userName: string;
  weight: string;
  description: string;
  grade: { id: number; name: string };
  gradeId: number;
  siteId: string;
  packingStyle: string;
  materialName?: string;
  totalInputWeight: string;
  type?: string;
  primaryProcessingId?: string;
};

export type RecycleReceive = {
  deliveryId: string | null;
  deliveriedDatetime: string;
  deliveryFromOperatorName: string;
  deliveryFromSiteName: string;
  grade: string;
  weight: string;
  targetType: string;
  targetIds: string[];
  description: string;
  ediNumber?: string;
  ediStatus?: EdiStatus;
  tenantName?: string;
};

export type RecycleReceiveData = {
  id: string;
  datetime: string;
  userId: string;
  userName: string;
  deliveryFromOperatorName: string;
  description: string;
  weight: string;
  targetType: string;
  targetIds: EdiIdMapper[];
  receptionMethod: string;
};

export type RecycleCompleteData = {
  id: string;
  datetime: string;
  userId: string;
  userName: string;
  materialType: { id: number; name: string };
  grade: { id: number; name: string };
  weight: string;
  targetType: string;
  targetIds: string[];
  description: string;
  materialTypeId: number;
  gradeId: number;
  siteId: string;
  totalInputWeight: string;
  deliverySetting?: boolean;
};

export type ScaleOperator = {
  id: string;
  name: string;
  siteId: string;
  waste: { id: string; name: string }[];
};

export type ScaleState = {
  operatorId: string;
  operatorName: string;
  siteId: string;
  wasteId: string;
  wasteName: string;
};

export type WasteUnit = {
  id: number;
  type: string;
  unit: string;
  quantity: string;
  convertKg: number;
  price: number;
  inputUnit: string;
};

export type CategoryItemEmissionType = {
  id: string;
  name: string;
  size: ItemSize;
  materialName: string | null;
  imageUrl?: string | null;
  convertKg: number | null;
  quantity: number | null;
  type: ItemType;
  unit: string | null;
};

export type CategoryItem = {
  id: number | string;
  name: string;
  other: boolean;
  imageUrl?: string | null;
  materialName: string | null;
  size: ItemSize;
  siteId: string;
  typeObject: 'category' | 'wasteUnit';
  convertKg: number | string | null;
  quantity: number | string | null;
  unit: string | null;
  wasteTypeMasterId?: number;
  recycleMethodId?: number;
  inputUnit?: string | null;
  type?: ItemType;
  categoryId?: string | number;
  categoryName?: string;
  substituteRegistRelations?: SubstituteRegistRelations[];
  wasteUnitsOneCategory?: CategoryItem[];
};

export type SubstituteRegistRelations = {
  fromSiteId: string;
  toSiteId: string;
};

export type CustomizeItemBreadcrumbs = {
  operatorName?: string;
  siteName?: string;
  categoryName?: string;
};

export type SectionFloorBreadcrumbs = {
  operatorName?: string;
  siteName?: string;
  sectionName?: string;
};

export type DeleteFloorSectionItem = {
  id: number;
  name: string;
  sectionId?: number;
};
export enum ItemType {
  OPTIONAL = 'Optional',
  FIX = 'Fixed',
}

export type SectionItem = {
  id: number;
  name: string;
  siteId: string;
};

export type FloorItem = {
  id: number;
  name: string;
  sectionId: number;
};

export type BrandSubItem = {
  id: number;
  name: string;
};

export type BrandItem = {
  id: string;
  name: string;
  address: string;
  imageUrl: string;
  operatorId: string;
  brandItems: BrandSubItem[];
  totalUnassignedItems?: number;
};

export type TareItem = {
  id: number;
  name: string;
  weight: string;
  siteId?: string;
  tenants?: {
    id: number;
    name?: string;
  }[];
};

export type DownloadResourceDataForm = {
  operatorType: OperatorType[];
  startDate: string;
  endDate: string;
};

export type OperatorTypeForm = {
  operatorType: OperatorType[];
};

export type SelectRangeDownloadForm = {
  startTime: string;
  endTime: string;
};

export type DistrictError = {
  key: string;
  error: boolean;
};

export type BarcodeICCardInput = {
  barcode: string;
  icCard: string;
};

export type BarcodeInput = {
  barcode: string;
};

export enum InvoiceStatus {
  Draft = 'Draft',
  Enqueue = 'Enqueue',
  Failed = 'Failed',
  Success = 'Success',
}

export type TareData = {
  page: number;
  total: number;
  tares: TareItem[];
};

export type InputString = string | undefined;

export type TCollectEmission = {
  id: string;
  operatorId: string;
  siteId: string;
  areaId: string;
  description: string;
  type: string;
  weight: string;
  datetime: string;
  wasteUnitQuantity: string;
  wasteUnit: TCollectWasteUnit;
  tenant?: {
    id: number;
    name: string;
  } | null;
  isEdiEnabled: boolean;
  collectionSiteId: string;
  collectionSiteName: string;
  materialName: string;
};

export type TCollectWasteUnit = {
  id: number;
  categoryId: number;
  wasteTypeMasterId: number;
  recycleMethodId: number;
  name: string;
  materialName: string;
  imageUrl: string;
  sortIndex: number;
  type: string;
  unit: string;
  quantity: string;
  convertKg: string;
  price: number;
  inputUnit: string;
  size: string;
  isEdiEnabled: boolean;
  registDatetime: string;
  registUserId: string;
  updateDatetime: string;
  updateUserId: string;
  ediLargeCategoryId?: string;
  ediMediumCategoryId?: string;
  ediSmallCategoryId?: string;
  ediDetailCategory?: string;
  ediWasteType?: string;
  wasteId?: string;
  operatorId?: string;
  deleteUserId?: string;
  deleteDatetime?: string;
};

export type TArea = {
  siteId: string;
  siteName: string;
  areaId: string;
  areaName: string;
  areaLongitude: string;
  areaLatitude: string;
  emissionFlg: boolean;
  collectRequestFlg: boolean;
  scalesType?: ScaleType;
  scalesModel?: ScalesModel;
};

export type TCreateCollectBySiteId = {
  id: string;
  data: {
    emissionIds: string[];
  };
};

export type Truck = {
  id: number;
  truckNumber: string;
};

export type ResTruck = {
  total: number;
  page: number;
  trucks: Truck[];
};

export type ProvincesDistricts = {
  province: string[];
  district: string[];
};

export type UserCompression = {
  id: number;
  name: string;
  site: {
    id: string;
    name: string;
  };
};

export type Option = {
  label: string;
  value: string;
};

export type UserCompressionInfo = {
  date: string;
  siteId: string;
  receives: Receive[];
  compressions: Receive[];
};

export type Receive = {
  id: string;
  type: string;
  weight: string;
};

export type UserCompressionCreateRequest = {
  gradeId: string | number;
  weight: string;
  tareWeight?: string;
  materialName: string;
  processingMethod: string;
  type: string;
  packingStyle: string;
  inputResources: Omit<PreRegisSelectedResource, 'materialName'>[];
  inputResourceIds?: string[]; // for auto compression
  prevProcessingObjectId?: string;
  isContinueProcessing?: boolean;
};

export type TSyncError = {
  token: string;
  refreshToken: string;
  records: WasteRegistrationOfflineRequest[];
  createdAt: string;
  status: number;
};

export type PurchaseReceive = {
  deliveryId: number;
  datetime: string;
  operatorName: string;
  areaName: string;
  weight: string;
  siteIds: string[];
  description: string;
};

export type CommonType = {
  id: number;
  name: string;
};

export type Breadcrumb = {
  label: string;
  href?: string;
};

export type PreRegisResource = {
  processingObjectId: string;
  materialName: string;
  remainingWeight: string;
  receivedDatetime: string;
  site: {
    id: string;
    name: string;
  };
  weight?: string;
  type?: string;
  grade?: {
    id: string;
    name: string;
  };
};

export type PreRegisSelectedResource = {
  processingObjectId: string;
  weight: string;
  materialName: string;
};

export type SelectedResources = Record<
  string,
  {
    weight: string;
    materialName: string;
    checked: boolean;
    hasError: boolean;
  }
>;

export type ResourcePreRegisFilter = {
  siteIds?: string[];
  receivedDatetime?: [string, string];
  materialName?: string;
  types?: string[];
};

export type ExternalScaleConnectionsHistoryItem = {
  id: string;
  linkDateTime: string;
  status: ImportStatus;
  importDateTime: string;
  fileSize: number;
};

export type TeraokaImportHistories = {
  page: number;
  total: number;
  data: ExternalScaleConnectionsHistoryItem[];
};

export type Station = {
  id: number;
  name: string;
};

export type Sites = {
  id: string;
  name: string;
  siteStations: Station[];
};

export type EmissionsSiteStation = {
  page: number;
  total: number;
  sites: Sites[];
};

export type AverageEmissionStatistics = {
  id: string;
  name: string;
  latitude: string;
  longitude: string;
  avgWeight: string;
  operatorName: string;
  siteStations: Station[];
};

export type DashboardsPoolResources = {
  emissionDatetime: string;
  siteStations: Station[];
  siteId: string;
  siteType: string;
  siteName: string;
  collectionSite?: CommonSite[];
  materialName: string;
  englishMaterialName: string;
  wasteTypeMaster: string;
  weight: string;
  id: string;
  siteBuildingType: string;
  receivedDatetime?: string;
  compressionSite?: {
    id: string;
    name: string;
    type: string;
  };
  wasteUnit?: {
    id: number;
    name: string;
  };
};

export type DashboardsPoolResourcesExportJob = {
  id: number;
  status: DashboardsPoolResourcesExportJobStatus;
  downloadUrl?: string;
};

export type RatioItem = {
  name: string;
  weight: string;
  rate: string;
  type?: string;
};

export type DashboardsPoolRatioData = {
  total: string;
  items: RatioItem[];
};

// Tenant
export type InputTenantItem = {
  id: string;
  name: string;
  price: number;
  display?: boolean;
  category?: CommonType;
};

export type TenantForm = {
  siteId: string;
  name: string;
  sectionId: string;
  floorId: string;
  brandId: string;
  industry: string;
  inputTenantItems: InputTenantItem[];
  tenantNumber: string;
  contactName: string;
  contactEmail: string;
  isDashboardEnabled: boolean;
  isEdiEnabled: boolean;
  ediSubscriberNumber: string;
  ediConfirmationKey: string;
  ediName: string;
  postalCode: string;
  province: string;
  district: string;
  address: string;
  staffTel: string;
  userId?: string;
  hiddenItems?: (number | string)[];
};

export type TenantSiteForm = {
  name: string;
  sectionId: string;
  floorId: string;
  industry: string;
  inputTenantItems: InputTenantItem[];
  tenantNumber: string;
  contactName: string;
  contactEmail: string;
  barcode: string;
  icCards: { card: string }[];
  userId?: string;
  isEdiEnabled: boolean;
  ediSubscriberNumber: string;
  ediConfirmationKey: string;
  ediName: string;
  postalCode: string;
  province: string;
  district: string;
  address: string;
  staffTel: string;
  hiddenItems?: (number | string)[];
};

// Export Data Invoice
export type FilterInvoiceData = {
  dateFrom: string;
  dateTo: string;
  stationId?: string | number;
};

export type TenantInvoiceItem = {
  itemName: string;
  weight: string;
  price: string;
  cost: string;
};

export type InvoiceData = {
  dateFrom: string;
  dateTo: string;
  invoices: {
    id: string;
    name: string;
    tenantNumber: string;
    totalCost: string;
    tenantItems: TenantInvoiceItem[];
  }[];
};

export type PoolInvoiceData = {
  dateFrom: string;
  dateTo: string;
  invoices: {
    month: string;
    siteId: string;
    siteName: string;
    totalCost: string;
    items: {
      itemName: string;
      cost: string;
      price: string;
      weight: string;
    }[];
  }[];
};

// Tenant Dashboards
export type RateByWasteUnit = {
  wasteUnitName: string;
  weight: number;
  rate: number;
};

export type DashboardsTenantsRateWasteUnits = {
  totalWeight: string;
  rateByWasteUnits: RateByWasteUnit[];
  itemMaxWeight?: string | null;
  recycleItemMinWeight?: string | null;
};

export type RateByRecycleMethod = {
  id: number;
  name: string;
  englishName: string;
  type: string;
  weight: string;
  rate: string;
};

export type DashboardsTenantsRateRecycleMethods = {
  totalWeight: string;
  totalRecycleRate: string;
  recycleMethods: RateByRecycleMethod[];
};

export type RateByWasteCost = {
  wasteUnitName: string;
  weight: string;
  price: number;
  cost: string;
  rate: string;
};

export type DashboardsTenantsRateWasteCosts = {
  totalCost: string;
  rateByWasteCost: RateByWasteCost[];
};

type WeightData = {
  name: string;
  weight: string;
};

export type DashboardsTenantsWastes = {
  date: string;
  day: number;
  month: number;
  year: number;
  data: WeightData[];
};

export type DashboardsTenantsCompareTotalEmissionWeight = {
  tenantName: string;
  totalWeight: string;
  rate: string;
};

export type DashboardsTenantsCompareRecycle = {
  siteName: string;
  tenantName: string;
  industry: string;
  currentMonth: string;
  previousMonth: string;
  siteRank: string;
  overallRank: OverallRank;
};

export type RateCO2ByWasteUnits = {
  wasteUnitName: string;
  CO2: string;
  rate: string;
};

export type DashboardsTenantsRateCO2WasteUnits = {
  totalCO2: string;
  rateCO2ByWasteUnits: RateCO2ByWasteUnits[];
};

export type TenantUpdateParams = Omit<Tenant, 'icCards'> & {
  icCards?: {
    id?: number;
    card: string;
  }[];
};

export type TenantBase = {
  id: number;
  name: string;
  section?: {
    id: number;
    name: string;
  };
  floor?: {
    id: number;
    name: string;
  };
};

export type GetEmissionParams = Pick<GetListParams, 'page' | 'take'> & {
  dateFrom?: string;
  dateTo?: string;
  collectedDateFrom?: string;
  collectedDateTo?: string;
  wasteUnitNames?: string[];
  tenantIds?: string[];
  tenantNames?: string[];
  status?: string[];
  siteIds?: string[];
  wasteUnitIds?: string[];
  temporaryStorageIds?: string[];
};

export type GetSubSiteResourcesFilter = {
  dateFrom?: string;
  dateTo?: string;
  tenantIds?: (string | null)[];
};

export type MfaSession = {
  session: string;
  email: string;
  expiresAt: string;
};

export type GetICCardScanHistoryParams = {
  page?: number;
  take?: number;
  dateFrom?: string;
  dateTo?: string;
};

export interface WheelPickerOption {
  value: string;
  label: string;
}

export interface NumberWheelProps {
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  decimalPlaces?: number;
  showDecimal?: boolean;
  onChange?: (value: number) => void;
  separator?: React.ReactNode | false;
  classNames?: {
    wrapper?: string;
    container?: string;
    separator?: string;
    integerWheel?: string;
    decimalWheel?: string;
  };
  wheelConfigs?: WheelPickerProps['configs'];
}

export interface WheelPickerProps {
  options: WheelPickerOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  infinite?: boolean;
  className?: string;
  configs?: {
    resistance?: number;
    maxVelocity?: number;
    visibleCount?: number;
    itemHeight?: number;
    dragSensitivity?: number;
    numberOfVisibleItems?: number;
  };
}

export type Area = {
  siteId: string;
  siteName: string;
  areaId: string;
  areaName: string;
  areaLongitude: string;
  areaLatitude: string;
  emissionFlg: boolean;
  collectRequestFlg: boolean;
  substituteRegistRelations: SubstituteRegistRelation[];
  sitePrintLabel: PrintLabel;
  type: 'collectingRoute';
  enableSubstituteRegist: boolean;
};

export type AreaDetail = Area & {
  tenants: Tenant[];
};

export type SubstituteRegistRelation = {
  wasteUnitId: string;
  toSiteId: string;
  fromSiteId: string;
  registFlg?: boolean;
};

export type AreaGroup = {
  id: string;
  name: string;
  type: 'groupSite';
  collectingRoutes?: Area[];
};

export type LoginHistoryGetParams = {
  siteId: string;
  dateFrom?: string;
  dateTo?: string;
};

export type ICCardScanHistories = {
  page: number;
  total: number;
  scanIcCardHistories: ICCardScanItem[];
};

export type ICCardScanItem = {
  cards: string[];
  scanAt: string;
};

export type TenantLoginHistory = {
  id: string;
  name: string;
  total: number;
};

export type OverallLoginHistory = {
  loginDate: string;
  total: number;
};

export type MasterProduct = {
  id: string;
  name: string;
  procurementPercentage: string;
  weight: string;
  siteId: string;
};

export type MasterProductResponse = {
  masterProducts: MasterProduct[];
  total: number;
  siteName: string;
  operatorName: string;
};

export type MasterProductForm = Omit<MasterProduct, 'id'>;

export type Collect = {
  id: number;
  collectedAreaId: string;
  collectedAreaName: string;
  collectedDatetime: string;
  deliveryUserId: string;
  deliveryUserName: string;
  weight: string;
  description: string;
  substituteRegist: boolean;
};

export type CollectListResponse = {
  page: number;
  total: number;
  deliveries: Collect[];
};

export type InputResourceQRResource = {
  processingObjectId: string;
  remainingWeight: string;
  weight?: string;
  wasteUnit?: {
    id: number;
    name: string;
  } | null;
};

export type InputResourceQRData = {
  deliveryFromSite: {
    id: string;
    name: string;
  };
  materialName: string;
  receivedDatetime: string;
  resources: InputResourceQRResource[];
};

export type CollectDelivery = {
  deliveryId: string;
  processingObjectId: string;
  weight: string;
  tenantId: string;
  tenantName: string;
  wasteUnitId: string;
  wasteUnitName: string;
};

export type CollectDeliveryResponse = {
  nextCursor: string | null;
  deliveryDetails: CollectDelivery[];
};

export type EmissionQRScannedResponse = {
  id: string;
  site: {
    id: string;
    name: string;
    printLabel: string;
  };
  emissionDatetime: string;
  emissionStatus: string;
  weight: string;
  materialName: string;
  wasteUnit?: CommonType;
  tenant?: CommonType;
  category?: CommonType;
  temporaryStorage?: CommonType & { color?: string };
  storageUpdatedAt?: string;
};

export type BatchRegistrationWasteUnit = {
  id: number;
  name: string;
  materialName: string;
  size: ItemSize;
  type: ItemType;
  substituteRegistRelations: SubstituteRegistRelation[];
  convertKg?: string | null;
  quantity?: number | null;
  unit?: string | null;
  inputUnit?: string | null;
  kind?: EmissionWasteUnitKind | null;
  imageUrl?: string | null;
};

export type BatchRegistrationLocalData = Record<string, (number | undefined)[]>;

export type UserBatchRegistrationLocalData = {
  userId: string;
  data: BatchRegistrationLocalData;
};

export type BookingQRScannedData = {
  deliveryId?: number;
  processingObjectId?: string;
  processingObjectIds: string[];
  weight: string;
  materialName: string;
  requestedDatetime: string;
  type: BookingQRType;
};

export type CompressionDeliveryQRSScannedData = {
  id: string;
  materialName: string;
  registDatetime: string;
  status: 'UnRequest' | 'Received';
  weight: string;
  totalInputWeight: string;
  qrCode: string;
  type?: string;
  grade?: {
    id: number;
    name: string;
  };
};

export type ScaleModelOptions = {
  ishida: SelectOption[];
  tanaka: SelectOption[];
};

export type ScalesModel = {
  name: string;
  tareMax?: string;
};

export type ScalesModelDetail = ScalesModel & {
  id: number;
  scalesType?: ScaleType;
};

export interface Floor {
  id: number;
  name: string;
  sectionId: number;
  tenants?: Tenant[];
}

export interface Section {
  id: number;
  name: string;
  siteId: string;
  floors?: Floor[];
  tenants?: Tenant[];
}

export interface Tenant {
  id: number;
  name: string;
  brand?: {
    id: string;
    name: string;
  };
  industry?: string;
  section?: Omit<Section, 'siteId' | 'floors' | 'tenants'>;
  floor?: Omit<Floor, 'sectionId' | 'tenants'>;
  barcode?: string;
  icCards?: {
    id: number;
    card: string;
  }[];
  tenantNumber?: string;
  contactName?: string;
  contactEmail?: string;
  isDashboardEnabled?: boolean;
  isEdiEnabled: boolean;
  ediName: string;
  ediSubscriberNumber: string;
  ediConfirmationKey: string;
  postalCode: string;
  province: string;
  district: string;
  address: string;
  staffTel: string;
  itemItems?: number[];
  sortIndex?: number;
  registDatetime: string;
  tenantItems?: number[];
}

export type ShipmentRegistrationFilter = {
  datetime?: [string, string];
  materialName?: string[];
  grade?: string[];
  kind?: string[];
};

export type ShipmentRegistrationRow = {
  id: string;
  processingDatetime: string;
  personInCharge: string;
  materialName: string;
  grade: string;
  kind: string;
  weight: string;
};

export type StorageShipmentRow = {
  processingObjectId: string;
  datetime: string;
  user: string;
  materialName: string;
  weight: string;
  localStorage: StorageLocalStorage | null;
};

export type StorageLocalStorage = {
  id: number;
  name: string;
  color: string;
};

export type StorageProcessingObject = {
  processingObjectId: string;
  datetime: string;
  userId: string;
  user: string;
  materialName: string;
  weight: string;
  localStorage: StorageLocalStorage;
};

export type ProcessingMaterialType = {
  typeName: string;
  weight: string;
  remainingWeight: string;
};

export type ProcessingMaterial = {
  processingObjectId: string;
  materialName: string;
  materialTypes: ProcessingMaterialType[];
};

export type ProcessingMaterialsResponse = {
  page: number;
  total: number;
  data: ProcessingMaterial[];
};

export type ProcessingMaterialsParams = {
  page: number;
  take: number;
  materialName?: string;
};

export type RawMaterialHistoryItem = {
  id: string;
  receivedDatetime: string;
  deliveriedDatetime?: string | null;
  place: string;
  materialName: string;
  itemName?: string;
  weight: string;
  measuredWeight?: string;
  containerWeight?: string;
  status: RawMaterialStatus;
  inventoryWeight?: string;
  type: string;
  editable: boolean;
  qrCode?: string | null;
  grade?: { id: string; name: string } | null;
};

export type RawMaterialHistoryResponse = {
  data: RawMaterialHistoryItem[];
  page: number;
  total: number;
};

export type RawMaterialHistoryParams = {
  dateFrom?: string;
  dateTo?: string;
  page: number;
  take: number;
};

export type ProcessedProductHistoryItem = {
  id: string;
  registDatetime: string;
  materialName: string;
  kind: string;
  grade: string;
  weight: string;
  inputResourceWeight: string;
  status: string;
  ediLinkage: string;
  ediNumber?: string;
  qrCode?: string | null;
  measuredWeight: string;
  containerWeight: string;
};

export type ProcessedProductHistoryResponse = {
  data: ProcessedProductHistoryItem[];
  total: number;
  page: number;
};

export type ProcessedProductHistoryParams = {
  dateFrom?: string;
  dateTo?: string;
  page: number;
  take: number;
};

export type StorageSiteHistoryItem = {
  receivedDatetime: string;
  fromPlace: string;
  itemName: string;
  materialName: string;
  weight: string;
  receivingMethod: StorageReceiveMethod;
  resourceId: string;
  storageLocation?: {
    id: number;
    name: string;
    color: string;
  };
  storageUpdateDatetime?: string;
  status: StorageHistoryStatus;
  shipmentDate?: string;
};

export type StorageSiteHistoryResponse = {
  data: StorageSiteHistoryItem[];
  page: number;
  total: number;
};

export type StorageSiteHistoryParams = {
  dateFrom?: string;
  dateTo?: string;
  page: number;
  take: number;
};
